<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useCurrency } from 'dashboard/composables/useCurrency';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import conversationProductsAPI from 'dashboard/api/conversationProducts';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// O painel "Negócios" reflete o sistema canônico de Funis (CFM): cada card do
// funil (ConversationFunnelMapping) é um negócio. Mostra funil · etapa · status
// · valor e os PRODUTOS cadastrados (conversation_products). O modelo Deal
// legado não é mais lido aqui (ficava sempre vazio).
const props = defineProps({
  conversationId: {
    type: [String, Number],
    required: true,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const { funnels, loadFunnels } = useFunnelManager();
const { currency: accountCurrency } = useCurrency();

// tradução com fallback PT fixo (sobrevive a chunk de locale velho em cache).
const tp = (key, fallback, args) => {
  const value = args ? t(key, args) : t(key);
  return value === key ? fallback : value;
};
const browserLocale = computed(() => locale.value.replace('_', '-'));

const loading = ref(false);
const mappings = ref([]);
const products = ref([]);

const fetchData = async () => {
  loading.value = true;
  try {
    const [mappingsRes, productsRes] = await Promise.all([
      // scope contact: o painel é do CONTATO — mostra cards de todas as
      // conversas dele (contato com 2 conversas não parece "sem negócio").
      ConversationFunnelMappingsAPI.getMappings(props.conversationId, {
        scope: 'contact',
      }),
      conversationProductsAPI.get(props.conversationId),
    ]);
    mappings.value = mappingsRes.data || [];
    products.value = productsRes.data?.payload || productsRes.data || [];
  } catch {
    mappings.value = [];
    products.value = [];
  } finally {
    loading.value = false;
  }
};

// Produtos do negócio: os que pertencem ao funil do card, mais os sem funil
// definido (ficam visíveis no primeiro negócio como "não atribuídos").
const productsForMapping = mapping =>
  products.value.filter(p => p.funnel_id === mapping.funnel_id || !p.funnel_id);

const sumProducts = list =>
  list.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);

// Valor do negócio: usa o deal_value do CFM; se vazio, soma dos produtos.
const dealValue = mapping => {
  if (mapping.deal_value) return parseFloat(mapping.deal_value);
  return sumProducts(productsForMapping(mapping));
};

// useMapGetter mantido para reatividade da conversa selecionada (troca de chat).
const currentChat = useMapGetter('getSelectedChat');

// --- Adicionar negócio (colocar a conversa num funil/etapa) ---
// Restaura o "+" que existia na antiga seção "Funis Múltiplos", removida
// quando o card Negócios foi consolidado — sem isso não havia como iniciar
// um negócio pela conversa.
const showAddForm = ref(false);
const isSaving = ref(false);
const selectedFunnelId = ref(null);
const selectedStage = ref(null);

const activeFunnels = computed(() => funnels.value.filter(f => f.active));

const availableFunnels = computed(() => {
  const mappedFunnelIds = mappings.value.map(m => m.funnel_id);
  return activeFunnels.value.filter(f => !mappedFunnelIds.includes(f.id));
});

const stagesForFunnel = funnelId => {
  const funnel = funnels.value.find(f => f.id === funnelId);
  if (!funnel?.stages) return [];
  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([key, stage]) => ({ key, label: stage.label, color: stage.color }));
};

const availableStages = computed(() => stagesForFunnel(selectedFunnelId.value));

const handleFunnelChange = () => {
  const funnel = funnels.value.find(f => f.id === selectedFunnelId.value);
  selectedStage.value = funnel?.settings?.default_stage || null;
};

const cancelAddForm = () => {
  selectedFunnelId.value = null;
  selectedStage.value = null;
  showAddForm.value = false;
};

const syncConversationInStore = (funnelId, stageName, mapping) => {
  const chat = currentChat.value;
  if (!chat || chat.id !== Number(props.conversationId)) return;
  const funnelMappings = [...(chat.funnel_mappings || [])];
  const index = funnelMappings.findIndex(m => m.id === mapping.id);
  if (index === -1) funnelMappings.push(mapping);
  else funnelMappings[index] = mapping;
  store.dispatch('updateConversation', {
    ...chat,
    funnel_mappings: funnelMappings,
    custom_attributes: {
      ...chat.custom_attributes,
      funnel_id: funnelId.toString(),
      stage_name: stageName,
    },
  });
};

const addToFunnel = async () => {
  if (!selectedFunnelId.value || !selectedStage.value) {
    useAlert(t('MULTIPLE_FUNNELS.MESSAGES.SELECT_FUNNEL_STAGE'));
    return;
  }
  isSaving.value = true;
  const funnelId = selectedFunnelId.value;
  const stageName = selectedStage.value;
  try {
    const response = await ConversationFunnelMappingsAPI.addToFunnel(
      props.conversationId,
      funnelId,
      stageName
    );
    if (response.data.success) {
      mappings.value.push(response.data.mapping);
      syncConversationInStore(funnelId, stageName, response.data.mapping);
      window.dispatchEvent(
        new CustomEvent('conversation-added-to-funnel', {
          detail: {
            conversationId: props.conversationId,
            funnelId,
            stageName,
            mapping: response.data.mapping,
          },
        })
      );
      cancelAddForm();
      useAlert(t('MULTIPLE_FUNNELS.MESSAGES.ADD_SUCCESS'));
    }
  } catch {
    useAlert(t('MULTIPLE_FUNNELS.MESSAGES.ADD_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const moveToStage = async (mapping, newStage) => {
  if (!newStage || newStage === mapping.stage_name) return;
  try {
    const response = await ConversationFunnelMappingsAPI.moveToStage(
      props.conversationId,
      mapping.id,
      newStage
    );
    if (response.data.success) {
      const index = mappings.value.findIndex(m => m.id === mapping.id);
      if (index !== -1) mappings.value[index] = response.data.mapping;
      syncConversationInStore(
        mapping.funnel_id,
        newStage,
        response.data.mapping
      );
      window.dispatchEvent(
        new CustomEvent('funnel-mapping-updated', {
          detail: {
            conversation_id: props.conversationId,
            funnel_id: mapping.funnel_id,
            stage_name: newStage,
            stage_label: response.data.mapping.stage_label,
            stage_color: response.data.mapping.stage_color,
            mapping: response.data.mapping,
          },
        })
      );
      useAlert(
        t('MULTIPLE_FUNNELS.MESSAGES.MOVE_SUCCESS', {
          stageName: response.data.mapping.stage_label,
        })
      );
    }
  } catch {
    useAlert(t('MULTIPLE_FUNNELS.MESSAGES.MOVE_ERROR'));
  }
};

const goToDeal = () => {
  const accountId = store.getters.getCurrentAccountId;
  router.push({
    name: 'pipeline_deal',
    params: { accountId, conversationId: props.conversationId },
  });
};

const formatCurrency = (amount, currencyCode = accountCurrency.value) => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currencyCode || accountCurrency.value,
  }).format(numAmount || 0);
};

// Dias na etapa atual (a partir de stage_entered_at).
const daysInStage = mapping => {
  if (!mapping.stage_entered_at) return null;
  const entered = new Date(mapping.stage_entered_at);
  if (Number.isNaN(entered.getTime())) return null;
  const diff = Math.floor((Date.now() - entered.getTime()) / 86400000);
  return diff >= 0 ? diff : null;
};

// MRR do negócio: metadata['mrr_value'] gravado pelo sync dos produtos.
const mrrValue = mapping => {
  const raw = mapping.metadata?.mrr_value;
  const value = typeof raw === 'string' ? parseFloat(raw) : raw;
  return value > 0 ? value : 0;
};

const statusClass = status => {
  const classes = {
    open: 'bg-n-brand/10 text-n-brand',
    won: 'bg-n-teal-3 text-n-teal-11',
    lost: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return classes[status] || classes.open;
};

const statusLabel = status => {
  const labels = {
    open: t('DEALS.STATUS.OPEN'),
    won: t('DEALS.STATUS.WON'),
    lost: t('DEALS.STATUS.LOST'),
  };
  return labels[status] || status;
};

watch(
  () => [props.conversationId, currentChat.value?.id],
  ([newId]) => {
    if (newId) fetchData();
  }
);

onMounted(async () => {
  await Promise.all([loadFunnels(), fetchData()]);
});
</script>

<template>
  <div class="conversation-deals">
    <div v-if="loading" class="flex justify-center p-4">
      <Spinner />
    </div>

    <div v-else class="p-4">
      <!-- Header -->
      <div class="mb-4 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t('DEALS.HEADER') }}
          </h4>
          <span
            v-if="mappings.length > 0"
            class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
          >
            {{ mappings.length }}
          </span>
        </div>
        <Button
          v-if="!showAddForm && availableFunnels.length > 0"
          data-testid="add-deal-button"
          size="sm"
          color="teal"
          icon="i-lucide-plus"
          :label="tp('DEALS.ADD', 'Adicionar negócio')"
          @click="showAddForm = true"
        />
      </div>

      <!-- Formulário: escolher funil + etapa para iniciar o negócio -->
      <div
        v-if="showAddForm"
        class="mb-3 flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('MULTIPLE_FUNNELS.ADD_FORM.SELECT_FUNNEL') }}
          </span>
          <select
            v-model="selectedFunnelId"
            data-testid="add-deal-funnel"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :disabled="isSaving"
            @change="handleFunnelChange"
          >
            <option :value="null">
              {{ t('KANBAN.SELECT_FUNNEL') }}
            </option>
            <option
              v-for="funnel in availableFunnels"
              :key="funnel.id"
              :value="funnel.id"
            >
              {{ funnel.name }}
            </option>
          </select>
        </label>

        <label v-if="selectedFunnelId" class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('MULTIPLE_FUNNELS.ADD_FORM.SELECT_STAGE') }}
          </span>
          <select
            v-model="selectedStage"
            data-testid="add-deal-stage"
            class="w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            :disabled="isSaving"
          >
            <option :value="null">
              {{ t('KANBAN.SELECT_STAGE') }}
            </option>
            <option
              v-for="stage in availableStages"
              :key="stage.key"
              :value="stage.key"
            >
              {{ stage.label }}
            </option>
          </select>
        </label>

        <div class="flex gap-2">
          <Button
            data-testid="add-deal-submit"
            size="sm"
            color="teal"
            :label="
              isSaving
                ? t('MULTIPLE_FUNNELS.ADD_FORM.ADDING')
                : t('MULTIPLE_FUNNELS.ADD_FORM.SUBMIT')
            "
            :disabled="isSaving || !selectedFunnelId || !selectedStage"
            @click="addToFunnel"
          />
          <Button
            size="sm"
            variant="faded"
            color="slate"
            :label="t('MULTIPLE_FUNNELS.ADD_FORM.CANCEL')"
            :disabled="isSaving"
            @click="cancelAddForm"
          />
        </div>
      </div>

      <!-- Negócios (cards do funil) -->
      <div v-if="mappings.length > 0" class="space-y-3">
        <!-- div clicável (não <button>): o card tem um <select> de etapa
             dentro, e elemento interativo dentro de <button> é HTML inválido -->
        <div
          v-for="mapping in mappings"
          :key="mapping.id"
          role="button"
          tabindex="0"
          class="block w-full cursor-pointer rounded-xl border border-n-weak bg-n-solid-1 p-3 text-left transition-colors hover:bg-n-alpha-1"
          @click="goToDeal"
          @keydown.enter="goToDeal"
        >
          <!-- Funil + status -->
          <div class="flex items-start justify-between gap-2">
            <span
              class="flex items-center gap-1.5 text-sm font-semibold text-n-slate-12"
            >
              <span class="i-lucide-git-fork rotate-90 text-n-slate-10" />
              {{ mapping.funnel_name }}
            </span>
            <span
              v-if="
                mapping.conversation_id &&
                mapping.conversation_id !== currentChat?.id
              "
              class="shrink-0 rounded-full bg-n-alpha-1 px-2 py-0.5 text-xs text-n-slate-10"
              :title="t('MULTIPLE_FUNNELS.STAGE.OTHER_CONVERSATION')"
            >
              {{ `#${mapping.conversation_display_id}` }}
            </span>
            <div class="flex shrink-0 items-center gap-1.5">
              <span
                v-if="mapping.sla_breached"
                class="rounded-full bg-n-ruby-3 px-2 py-0.5 text-xs font-medium text-n-ruby-11"
              >
                {{ t('MULTIPLE_FUNNELS.STAGE.SLA_BREACHED') }}
              </span>
              <span
                class="rounded-full px-2 py-0.5 text-xs font-medium"
                :class="statusClass(mapping.deal_status)"
              >
                {{ statusLabel(mapping.deal_status) }}
              </span>
            </div>
          </div>

          <!-- Etapa (editável) + dias na etapa -->
          <div class="mt-1.5 flex items-center gap-1.5 text-xs">
            <span
              v-if="mapping.stage_color"
              class="h-2 w-2 shrink-0 rounded-full"
              :style="{ backgroundColor: mapping.stage_color }"
            />
            <select
              v-if="stagesForFunnel(mapping.funnel_id).length > 0"
              data-testid="deal-stage-select"
              :value="mapping.stage_name"
              class="rounded border border-n-weak bg-n-background px-1.5 py-0.5 text-xs text-n-slate-11 outline-none focus:border-n-brand"
              @click.stop
              @change="moveToStage(mapping, $event.target.value)"
            >
              <option
                v-for="stage in stagesForFunnel(mapping.funnel_id)"
                :key="stage.key"
                :value="stage.key"
              >
                {{ stage.label }}
              </option>
            </select>
            <span v-else class="text-n-slate-11">
              {{ mapping.stage_label || mapping.stage_name }}
            </span>
            <span v-if="daysInStage(mapping) !== null" class="text-n-slate-10">
              ·
              {{
                tp(
                  'DEALS.DAYS_IN_STAGE',
                  `${daysInStage(mapping)}d nesta etapa`,
                  { count: daysInStage(mapping) }
                )
              }}
            </span>
          </div>

          <!-- Produtos cadastrados -->
          <div
            v-if="productsForMapping(mapping).length > 0"
            class="mt-2.5 space-y-1 border-t border-n-weak pt-2.5"
          >
            <p
              class="flex items-center gap-1 text-[11px] font-medium text-n-slate-10"
            >
              <span class="i-lucide-package text-xs" />
              {{ t('DEALS.PRODUCTS') }}
            </p>
            <div
              v-for="product in productsForMapping(mapping)"
              :key="product.id"
              class="flex items-center justify-between gap-2 text-xs"
            >
              <span class="truncate text-n-slate-11">{{ product.name }}</span>
              <span class="shrink-0 tabular-nums text-n-slate-12">
                {{ formatCurrency(product.amount, product.currency) }}
              </span>
            </div>
          </div>

          <!-- Valor total + MRR -->
          <div class="mt-2.5 border-t border-n-weak pt-2.5">
            <div class="flex items-center justify-between">
              <span class="text-xs text-n-slate-10">{{
                t('DEALS.FORM.AMOUNT')
              }}</span>
              <span class="text-base font-bold tabular-nums text-n-teal-11">
                {{ formatCurrency(dealValue(mapping)) }}
              </span>
            </div>
            <div
              v-if="mrrValue(mapping) > 0"
              class="mt-1 flex items-center justify-between"
            >
              <span class="text-[11px] text-n-slate-10">{{
                tp('DEALS.MRR', 'Recorrente (MRR)')
              }}</span>
              <span class="text-xs font-semibold tabular-nums text-n-teal-11">
                {{ `${formatCurrency(mrrValue(mapping))}/mês` }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Estado vazio: só quando não há negócio no funil -->
      <div v-else class="py-8 text-center">
        <span
          class="i-lucide-briefcase mb-2 inline-block text-3xl text-n-slate-8"
        />
        <p class="text-sm text-n-slate-11">
          {{ t('DEALS.EMPTY_STATE.TITLE') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-10">
          {{
            tp(
              'DEALS.EMPTY_STATE.CFM_HINT',
              'Use o botão Adicionar negócio para colocar esta conversa em um funil'
            )
          }}
        </p>
        <p
          v-if="activeFunnels.length === 0"
          class="mt-1 text-xs text-n-slate-10"
        >
          {{ t('MULTIPLE_FUNNELS.INFO.NO_FUNNELS_AVAILABLE') }}
        </p>
      </div>
    </div>
  </div>
</template>
